import { AlertCircle, Check } from "@untitledui/icons";
import type { ComponentType, InputHTMLAttributes, ReactNode } from "react";
import { cx } from "@/utils/cx";

export const WIZARD_STEPS = [
  { label: "Add merchant details", caption: "Enter your merchant's details" },
  { label: "Detect risks", caption: "Tell us what to monitor" },
  { label: "Configure risks", caption: "Decide what happens next" },
  { label: "Connect TikTok shop", caption: "Approve access on TikTok" },
  { label: "Review & submit", caption: "Confirm and finish" },
] as const;

type StepState = "done" | "current" | "upcoming" | "warning";
type StepPosition = "first" | "middle" | "last";

const NODE_CLASSES: Record<StepState, string> = {
  warning: "border-warning-300 bg-warning-50 text-warning-700",
  done: "border-brand-950 bg-brand-950 text-white",
  current: "border-brand-950 bg-white ring-4 ring-brand-100",
  upcoming: "border-gray-200 bg-white",
};

const LABEL_CLASSES: Record<StepState, string> = {
  warning: "text-warning-700",
  done: "text-gray-900",
  current: "text-brand-700",
  upcoming: "text-gray-500",
};

const ITEM_ALIGN: Record<StepPosition, string> = {
  first: "items-start",
  middle: "items-center",
  last: "items-end",
};

const TEXT_ALIGN: Record<StepPosition, string> = {
  first: "text-left",
  middle: "text-center",
  last: "text-right",
};

const stepState = (number: number, current: number, warningStep?: number): StepState => {
  if (number === current) return "current";
  if (number === warningStep) return "warning";
  return number < current ? "done" : "upcoming";
};

const stepPosition = (index: number): StepPosition => {
  if (index === 0) return "first";
  return index === WIZARD_STEPS.length - 1 ? "last" : "middle";
};

const StepNodeContent = ({ state }: { state: StepState }) => {
  if (state === "warning") return <AlertCircle className="size-4" />;
  if (state === "done") return <Check className="size-4" strokeWidth={3} />;
  return <span className={cx("size-2.5 rounded-full", state === "current" ? "bg-brand-950" : "bg-gray-300")} />;
};

export const WizardProgress = ({
  current,
  warningStep,
  onSelect,
  reachable,
}: {
  current: number;
  warningStep?: number;
  onSelect: (step: number) => void;
  reachable: (step: number) => boolean;
}) => (
  <ol className="relative grid grid-cols-5 min-h-[92px]" aria-label="Add merchant progress">
    {WIZARD_STEPS.map((step, index) => {
      const number = index + 1;
      const state = stepState(number, current, warningStep);
      const position = stepPosition(index);
      return (
        <li key={step.label} className="relative flex flex-col" aria-current={state === "current" ? "step" : undefined}>
          {position !== "first" && (
            <span
              className={cx(
                "absolute top-4 left-0 h-0.5 -translate-y-1/2",
                position === "last" ? "right-4" : "right-1/2",
                number <= current ? "bg-brand-950" : "bg-gray-200",
              )}
              aria-hidden="true"
            />
          )}
          {position !== "last" && (
            <span
              className={cx(
                "absolute top-4 right-0 h-0.5 -translate-y-1/2",
                position === "first" ? "left-4" : "left-1/2",
                number < current ? "bg-brand-950" : "bg-gray-200",
              )}
              aria-hidden="true"
            />
          )}
          <button
            type="button"
            onClick={() => onSelect(number)}
            disabled={!reachable(number) || state === "current"}
            className={cx("relative z-10 flex flex-col gap-3 disabled:cursor-default", ITEM_ALIGN[position])}
          >
            <span
              className={cx(
                "flex size-8 items-center justify-center rounded-full border-2 transition-colors",
                NODE_CLASSES[state],
              )}
            >
              <StepNodeContent state={state} />
            </span>
            <span className={cx("text-sm font-semibold whitespace-nowrap", LABEL_CLASSES[state])}>{step.label}</span>
          </button>
          {state === "current" && (
            <span className={cx("mt-1 text-xs text-gray-500 whitespace-nowrap", TEXT_ALIGN[position])}>
              {step.caption}
            </span>
          )}
        </li>
      );
    })}
  </ol>
);

export const FieldRow = ({
  label,
  description,
  required,
  children,
  htmlFor,
}: {
  label: string;
  description: ReactNode;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) => (
  <div className="grid grid-cols-1 md:grid-cols-[1fr_440px] gap-4 md:gap-14 py-7 border-b border-secondary last:border-b-0">
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-semibold text-primary">
        {label}
        {required && <span className="ml-1 text-error-600">*</span>}
      </label>
      <div className="text-sm text-tertiary">{description}</div>
    </div>
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

export const TextInput = ({
  isInvalid,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { isInvalid?: boolean }) => (
  <input
    {...props}
    aria-invalid={isInvalid || undefined}
    className={cx(
      "h-10 w-full rounded-lg border bg-white px-3.5 text-md text-primary shadow-xs outline-none transition-colors placeholder:text-placeholder",
      "focus:border-brand-500 focus:ring-4 focus:ring-brand-100",
      isInvalid ? "border-warning-400" : "border-primary",
      className,
    )}
  />
);

export const ChoiceCard = ({
  icon: Icon,
  title,
  description,
  badge,
  selected,
  isInvalid,
  onSelect,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  badge?: ReactNode;
  selected: boolean;
  isInvalid?: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={cx(
      "flex min-w-[260px] flex-1 items-start gap-3 rounded-xl border bg-white p-5 text-left transition-colors duration-100",
      selected && "border-brand-950 ring-1 ring-brand-950",
      !selected && isInvalid && "border-warning-400 ring-1 ring-warning-400",
      !selected && !isInvalid && "border-secondary hover:border-primary",
    )}
  >
    <Icon className="mt-0.5 size-5 shrink-0 text-secondary" />
    <span className="flex flex-1 flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-primary">
        {title}
        {badge}
      </span>
      <span className="text-sm text-tertiary">{description}</span>
    </span>
    <span
      className={cx(
        "flex size-5 shrink-0 items-center justify-center rounded-full border",
        selected ? "border-brand-950 bg-brand-950" : "border-primary bg-white",
      )}
      aria-hidden="true"
    >
      {selected && <Check className="size-3 text-white" strokeWidth={3} />}
    </span>
  </button>
);

export const StepHeading = ({ title, description }: { title: string; description: ReactNode }) => (
  <div className="flex flex-col gap-1">
    <h1 className="text-display-xs font-medium text-primary">{title}</h1>
    <p className="text-sm text-gray-600">{description}</p>
  </div>
);

export const DarkInfoCard = ({
  icon: Icon,
  title,
  children,
}: {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  children: ReactNode;
}) => (
  <div
    className="flex flex-wrap items-center gap-4 rounded-xl px-6 py-5 text-white"
    style={{ background: "linear-gradient(120deg, #2e1b40 0%, #3d2353 100%)" }}
  >
    {Icon && <Icon className="size-5 shrink-0 self-start text-brand-200" />}
    <div className="flex flex-1 flex-col gap-1 min-w-[240px]">
      <p className="text-sm font-semibold">{title}</p>
      <div className="text-sm text-brand-100">{children}</div>
    </div>
  </div>
);
